// Tipos compartidos entre el navegador y las Pages Functions.
// Nada de este archivo puede tocar document ni window.

export interface Sesion {
  usuario: string;
}

// --------------------------------------------------------------- catalogos
// Las listas son valores (las usa zod en el backend y los <select> en el
// frontend); los tipos salen de ellas para que no se puedan desincronizar.

export const ESTADOS_PROYECTO = [
  "IDEA",
  "EN_DESARROLLO",
  "EN_PRODUCCION",
  "PAUSADO",
  "ARCHIVADO",
] as const;
export type EstadoProyecto = (typeof ESTADOS_PROYECTO)[number];

/**
 * Colores con los que se marca un proyecto para reconocerlo de un vistazo.
 *
 * Es una marca, no un estado: no quiere decir nada de la urgencia ni del
 * avance. Se guarda el nombre, nunca un hex libre; el tono exacto lo elige
 * `src/styles/app.css`, para que el único color de alarma siga siendo la
 * terracota de lo vencido.
 */
export const COLORES_PROYECTO = [
  "AZUL",
  "CIAN",
  "VERDE",
  "LIMA",
  "AMBAR",
  "NARANJA",
  "ROSA",
  "VIOLETA",
] as const;
export type ColorProyecto = (typeof COLORES_PROYECTO)[number];

export const TIPOS_TAREA = [
  "MODIFICACION",
  "BUG",
  "MEJORA",
  "TRAMITE",
  "PAGO",
  "INVESTIGACION",
] as const;
export type TipoTarea = (typeof TIPOS_TAREA)[number];

export const ESTADOS_TAREA = ["PENDIENTE", "EN_CURSO", "HECHA", "ANULADA"] as const;
export type EstadoTarea = (typeof ESTADOS_TAREA)[number];

/**
 * Prioridades: 1 es lo más importante, 10 lo que puede esperar.
 *
 * Escala 1 a 10 desde agosto de 2026 (antes 1 a 5). Los valores viejos siguen
 * valiendo lo mismo: un P3 de antes es el P3 de ahora.
 */
export const PRIORIDADES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

export const PERIODICIDADES = ["UNICO", "MENSUAL", "TRIMESTRAL", "ANUAL"] as const;
export type Periodicidad = (typeof PERIODICIDADES)[number];

export const ESTADOS_PRESUPUESTO = [
  "BORRADOR",
  "ENVIADO",
  "ACEPTADO",
  "RECHAZADO",
  "ANULADO",
] as const;
export type EstadoPresupuesto = (typeof ESTADOS_PRESUPUESTO)[number];

/** VENCIDO se calcula desde fecha + validez_dias; no se guarda. */
export type EstadoPresupuestoEfectivo = EstadoPresupuesto | "VENCIDO";

export const ESTADOS_CONTRATO = ["BORRADOR", "ENVIADO", "FIRMADO", "ANULADO"] as const;
export type EstadoContrato = (typeof ESTADOS_CONTRATO)[number];

export const MONEDAS = ["UYU", "USD"] as const;
export type Moneda = (typeof MONEDAS)[number];

export const CATEGORIAS_CONDICION = [
  "PAGO",
  "PLAZOS",
  "ALCANCE",
  "SOPORTE",
  "PROPIEDAD",
  "HOSTING",
] as const;
export type CategoriaCondicion = (typeof CATEGORIAS_CONDICION)[number];

/** De quién es la tarea: mía o del cliente. */
export const DESTINATARIOS = ["DESARROLLADOR", "CLIENTE"] as const;
export type Destinatario = (typeof DESTINATARIOS)[number];

/** De dónde salió la tarea. DICTADO deja el texto crudo en `detalle`. */
export const ORIGENES_TAREA = ["MANUAL", "DICTADO", "CONDICION"] as const;
export type OrigenTarea = (typeof ORIGENES_TAREA)[number];

/** El estado que se muestra. BLOQUEADA se calcula en v_tarea, no se guarda. */
export type EstadoEfectivo = EstadoTarea | "BLOQUEADA";

// --------------------------------------------------------------- entidades

export interface Cliente {
  id: number;
  nombre: string;
  contacto: string | null;
  email: string | null;
  telefono: string | null;
  notas: string | null;
  activo: number;
}

export interface Proyecto {
  id: number;
  nombre: string;
  /** Corto, 1 a 4 caracteres. Lo que se dice primero al dictar: B, PP, MM. */
  codigo: string | null;
  /** Cómo se lo nombra en voz alta, separado por comas. */
  alias: string | null;
  /** Marca visual para reconocerlo en la lista. NULL = sin color. */
  color: ColorProyecto | null;
  cliente_id: number | null;
  cliente_nombre: string | null;
  estado: EstadoProyecto;
  prioridad: number;
  url_prod: string | null;
  url_repo: string | null;
  stack: string | null;
  notas: string | null;
  activo: number;
  /**
   * Cuándo cayó al tacho, o NULL si no está en el tacho.
   *
   * Es lo único que distingue "en el tacho" de "archivado": los dos son
   * `activo = 0`. La diferencia que importa es que tirar al tacho **no pisa el
   * estado**, y por eso volver del tacho devuelve el proyecto igual que estaba.
   */
  papelera_el: string | null;
  creado_en: string;
}

/** Fila de la lista de proyectos: el proyecto con sus tareas contadas. */
export interface ProyectoListado extends Proyecto {
  pendientes: number;
  bloqueadas: number;
}

/** Fila del tacho: además de lo de arriba, de qué se agarra y qué lo frena. */
export interface ProyectoEnPapelera {
  id: number;
  nombre: string;
  estado: EstadoProyecto;
  color: ColorProyecto | null;
  papelera_el: string;
  tareas: number;
  notas: number;
  presupuestos: number;
  contratos: number;
  /** Por qué no se va a poder borrar, ya escrito para leer. NULL = se borra. */
  motivo_frena: string | null;
}

export interface Tarea {
  id: number;
  /** NULL = Bandeja de entrada: se capturó sin proyecto reconocible. */
  proyecto_id: number | null;
  proyecto_nombre?: string | null;
  origen: OrigenTarea;
  titulo: string;
  detalle: string | null;
  tipo: TipoTarea;
  prioridad: number;
  estado: EstadoTarea;
  vence_el: string | null;
  bloqueada_por: number | null;
  hecha_el: string | null;
  creado_en: string;
  estado_efectivo: EstadoEfectivo;
  bloqueante_titulo: string | null;
  para: Destinatario;
  origen_presupuesto_id: number | null;
  origen_condicion_id: number | null;
  /** Estimación cargada a mano. La app no mide tiempo. */
  horas_estimadas: number | null;
}

/** Nota fechada de un proyecto: la memoria de "en qué estaba". */
export interface NotaBitacora {
  id: number;
  proyecto_id: number;
  proyecto_nombre: string;
  fecha: string;
  texto: string;
  etiquetas: string | null;
  activo: number;
}

export interface CondicionTarea {
  id: number;
  condicion_id: number;
  titulo_tarea: string;
  tipo: TipoTarea;
  dias_offset: number;
  para: Destinatario;
}

export interface Condicion {
  id: number;
  titulo: string;
  texto: string;
  categoria: CategoriaCondicion;
  activo: number;
  tareas: number; // cuántas tareas dispara
}

export interface FichaCondicion {
  condicion: Condicion;
  tareas: CondicionTarea[];
}

/** Una cláusula adjunta a un presupuesto. */
export interface CondicionAdjunta {
  condicion_id: number;
  titulo: string;
  texto: string;
  categoria: CategoriaCondicion;
  orden: number;
  texto_override: string | null;
}

/** Un vínculo libre con otro proyecto (tabla relacion). */
export interface ProyectoRelacionado {
  id: number; // id de la relación, para poder sacarla
  otro_id: number;
  otro_nombre: string;
}

/** Lo que devuelve GET /api/proyectos/[id]. */
export interface FichaProyecto {
  proyecto: Proyecto;
  tareas: Tarea[];
  relacionados: ProyectoRelacionado[];
  bitacora: NotaBitacora[];
}

export interface Recordatorio {
  id: number;
  proyecto_id: number | null;
  proyecto_nombre: string | null;
  titulo: string;
  proxima_fecha: string;
  periodicidad: Periodicidad;
  avisar_dias_antes: number;
  activo: number;
}

/** Fila de v_agenda: una tarea con vencimiento o un recordatorio. */
export interface ItemAgenda {
  origen: "TAREA" | "RECORDATORIO";
  id: number;
  titulo: string;
  fecha: string;
  mostrar_desde: string;
  proyecto_id: number | null;
  proyecto_nombre: string | null;
  /** El color del proyecto, para pintar la tarjeta de la pantalla Hoy. */
  proyecto_color: ColorProyecto | null;
  detalle: string;
  prioridad: number;
  estado_efectivo: EstadoEfectivo;
}

export interface Bloqueante {
  id: number;
  titulo: string;
  tipo: TipoTarea;
  proyecto_id: number;
  proyecto_nombre: string;
  vence_el: string | null;
  frena_a: number;
}

export interface PresupuestoItem {
  id: number;
  presupuesto_id: number;
  descripcion: string;
  cantidad: number;
  precio_unitario: number; // centavos
  orden: number;
}

export interface Presupuesto {
  id: number;
  numero: number;
  cliente_id: number;
  cliente_nombre: string;
  proyecto_id: number | null;
  proyecto_nombre: string | null;
  fecha: string;
  estado: EstadoPresupuesto;
  estado_efectivo: EstadoPresupuestoEfectivo;
  moneda: Moneda;
  validez_dias: number;
  vence_el: string;
  notas: string | null;
  enviado_el: string | null;
  total: number; // centavos, calculado
  items: number; // cantidad de renglones
}

/** Lo que devuelve GET /api/presupuestos/[id]. */
export interface FichaPresupuesto {
  presupuesto: Presupuesto;
  items: PresupuestoItem[];
  condiciones: CondicionAdjunta[];
}

export interface PresupuestoAbierto extends Presupuesto {
  dias_esperando: number;
}

/**
 * Un contrato. Es una FOTO congelada del presupuesto que lo originó: el monto
 * y las cláusulas se copian al generarlo y no se recalculan nunca.
 */
export interface Contrato {
  id: number;
  numero: number;
  cliente_id: number;
  cliente_nombre: string;
  proyecto_id: number | null;
  proyecto_nombre: string | null;
  presupuesto_id: number | null;
  presupuesto_numero: number | null;
  fecha: string;
  estado: EstadoContrato;
  moneda: Moneda;
  monto: number; // centavos, congelado al generar
  objeto: string | null;
  plazo_dias: number | null;
  fecha_inicio: string | null;
  notas: string | null;
  enviado_el: string | null;
  firmado_el: string | null;
  clausulas: number; // cantidad, de v_contrato
}

/** Una cláusula copiada al contrato. Editable mientras es BORRADOR. */
export interface ClausulaContrato {
  id: number;
  contrato_id: number;
  orden: number;
  titulo: string;
  texto: string;
  condicion_id: number | null;
}

/** Lo que devuelve GET /api/contratos/[id]. */
export interface FichaContrato {
  contrato: Contrato;
  clausulas: ClausulaContrato[];
}

/** Lo que devuelve GET /api/agenda: la pantalla principal ya armada. */
export interface Agenda {
  hoy: string;
  /** Cuántas notas esperan en la Bandeja. 0 = no se muestra nada. */
  bandeja: number;
  vencido_hoy: ItemAgenda[];
  semana: ItemAgenda[];
  avisados: ItemAgenda[];
  bloqueantes: Bloqueante[];
  presupuestos: PresupuestoAbierto[];
  proyectos: ProyectoListado[];
}
